import { InputField, type InputFieldProps } from '@components/InputField';
import { InputMultiSelectInner } from '@components/InputMultiSelect/InputMultiSelectInner';
import type { UIOnChangeFn, UISelectionType } from '@components/types';

import InputReadOnly from '../InputReadOnly';

import '../InputMultiSelect.css';

export interface InputMultiSelectProps<T = string> extends InputFieldProps {
  dataTestId?: string;
  'aria-labelledby'?: string;
  dropdownInnerWrapperClassName?: string;
  dropdownWrapperClassName?: string;
  formatter?: (value: string) => string;
  initialSelectedItem?: T[];
  isReadOnly?: boolean;
  isSearchable?: boolean;
  itemFormatter?: (value: string) => string;
  menuOuterClassName?: string;
  multiSelectDropdownClasses?: string;
  onChange: UIOnChangeFn<T[]>;
  onSelect?: (args: unknown) => void;
  options: readonly string[] | readonly UISelectionType<T>[];
  placeholder?: string;
  selectedItemClassName?: string;
  selectedItemLabelClassName?: string;
  remainingItemsClassName?: string;
  remainingTriggerClassName?: string;
  shouldHideTitle?: boolean;
  shouldRenderInsidePortal?: boolean;
  value?: string | T[];
}

const InputMultiSelect = <T = string,>({
  className,
  dataTestId,
  descriptionText,
  dropdownInnerWrapperClassName,
  dropdownWrapperClassName,
  errors,
  helperText,
  id,
  isIncomplete,
  isReadOnly,
  isSearchable = true,
  labelText,
  menuOuterClassName,
  multiSelectDropdownClasses,
  name = 'multiselect',
  onChange,
  onSelect: _onSelect,
  options = [],
  placeholder = 'Select',
  selectedItemClassName,
  selectedItemLabelClassName,
  remainingItemsClassName,
  remainingTriggerClassName,
  shortName,
  shouldHideTitle,
  shouldRenderInsidePortal,
  size = 'md',
  value = [],
  ...props
}: InputMultiSelectProps<T>) => {
  return (
    <InputField
      className={className}
      descriptionText={descriptionText}
      errors={errors}
      helperText={helperText}
      id={id}
      labelText={shouldHideTitle ? '' : labelText}
      name={name}
      shortName={shortName}
      {...props}
    >
      {isReadOnly && (
        <InputReadOnly
          {...props}
          aria-describedby={descriptionText ? `${name}Description` : undefined}
          name={name}
          value={Array.isArray(value) ? value.join(', ') : value}
        />
      )}

      {!isReadOnly && (
        <InputMultiSelectInner
          dataTestId={dataTestId}
          dropdownInnerWrapperClassName={dropdownInnerWrapperClassName}
          dropdownWrapperClassName={dropdownWrapperClassName}
          id={id}
          isDisabled={props.isDisabled}
          isIncomplete={isIncomplete}
          isSearchable={isSearchable}
          menuOuterClassName={menuOuterClassName}
          multiSelectDropdownClasses={multiSelectDropdownClasses}
          name={name}
          onChange={onChange}
          options={options}
          placeholder={placeholder}
          selectedItemClassName={selectedItemClassName}
          selectedItemLabelClassName={selectedItemLabelClassName}
          remainingItemsClassName={remainingItemsClassName}
          remainingTriggerClassName={remainingTriggerClassName}
          shouldRenderInsidePortal={shouldRenderInsidePortal}
          size={size}
          value={value}
        />
      )}
    </InputField>
  );
};

export default InputMultiSelect;
